<!-- modal: the drawer does NOT block the page behind it otherwise, so with dismissible off the
     admin can keep clicking the grid and the tabs underneath a form that is still filled in and
     still floating over them — and every one of those clicks silently does something. -->
<p-drawer
  styleClass="floatbar booking-drawer"
  [(visible)]="visible"
  [position]="position"
  [style]="{ width: '480px', maxWidth: '96vw' }"
  [modal]="true"
  [dismissible]="false"
  [showCloseIcon]="false"
>
  @if (visible) {
    <form [formGroup]="form" (ngSubmit)="save()" class="sidebar-content booking-form">
      <header class="floatbar-header">
        <div class="row">
          <div>
            <h2 class="no-mg">{{ (isNew ? 'BOOKINGS.FORM.CREATE_TITLE' : 'BOOKINGS.FORM.EDIT_TITLE') | translate }}</h2>
          </div>
          <div class="row grid-xsm">
            <div class="cell">
              <button type="button" class="btn btn-link" (click)="close()">{{ 'COMMON.BUTTONS.CANCEL' | translate }}</button>
            </div>
            <div class="cell">
              <button type="submit" class="btn btn-primary btn-submit" [disabled]="form.invalid || invalidRange || saving">
                {{ (isNew ? 'COMMON.BUTTONS.CREATE' : 'COMMON.BUTTONS.SAVE') | translate }}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div class="floatbar-body booking-form-body">
        <section class="form-section">
          <h3 class="form-section-title">{{ 'BOOKINGS.FORM.SECTION_WHO' | translate }}</h3>

          <div class="form-group">
            <label class="field-label" for="booking-room">{{ 'BOOKINGS.FIELDS.ROOM' | translate }}</label>
            <p-select
              inputId="booking-room"
              formControlName="area_id"
              optionLabel="name"
              optionValue="area_id"
              optionGroupLabel="label"
              optionGroupChildren="items"
              [options]="hasMultipleBranches ? areaGroups : areas"
              [group]="hasMultipleBranches"
              [filter]="true"
              filterBy="name"
              [placeholder]="'BOOKINGS.FORM.PICK_ROOM' | translate"
              (onChange)="onAreaChange()"
            ></p-select>
            @if (!canChangeRoomAndPass) {
              <small class="field-hint">{{ 'BOOKINGS.FORM.ROOM_LOCKED' | translate }}</small>
            }
          </div>

          <!-- The room's own rules, as the four numbers they actually are. PAPI enforces every one
               of them on save, so showing them here is what turns a refusal into a choice. -->
          @if (selectedArea) {
            <!-- Every number carries its own dir="ltr": a numeric RANGE around a neutral dash is
                 reordered into "240–30" by the bidi algorithm in a Hebrew paragraph. Isolating just
                 the digits and leaving the unit as ordinary translated text keeps both correct. -->
            <dl class="room-rules">
              <div>
                <dt>{{ 'BOOKINGS.FORM.RULES.SLOT' | translate }}</dt>
                <dd><span dir="ltr">{{ selectedArea.slot_minutes }}</span> {{ 'BOOKINGS.FORM.RULES.MINUTES_UNIT' | translate }}</dd>
              </div>
              <div>
                <dt>{{ 'BOOKINGS.FORM.RULES.LENGTH' | translate }}</dt>
                <dd>
                  <span dir="ltr">{{ selectedArea.min_duration_minutes }}&ndash;{{ selectedArea.max_duration_minutes }}</span>
                  {{ 'BOOKINGS.FORM.RULES.MINUTES_UNIT' | translate }}
                </dd>
              </div>
              <div>
                <dt>{{ 'BOOKINGS.FORM.RULES.RATE' | translate }}</dt>
                <dd><span dir="ltr">{{ selectedArea.credit_rate_per_hour }}</span> {{ 'BOOKINGS.FORM.RULES.RATE_UNIT' | translate }}</dd>
              </div>
              @if (selectedArea.capacity) {
                <div>
                  <dt>{{ 'BOOKINGS.FORM.RULES.SEATS' | translate }}</dt>
                  <dd>{{ selectedArea.capacity }}</dd>
                </div>
              }
            </dl>
          }

          <div class="form-group">
            <label class="field-label" for="booking-pass">{{ 'BOOKINGS.FIELDS.BOOKED_BY' | translate }}</label>

            @if (canChangeRoomAndPass) {
              <!-- p-autoComplete, not a filtered p-select: the matching runs on the server (the name
                   and phone columns are encrypted, so only PAPI can compare a decrypted substring),
                   and a select would re-filter the answers against its own labels — silently
                   dropping every phone-number hit. An autocomplete takes its suggestions as final. -->
              <p-autoComplete
                inputId="booking-pass"
                [(ngModel)]="selectedCandidate"
                [ngModelOptions]="{ standalone: true }"
                [suggestions]="candidates"
                (completeMethod)="searchCandidates($event)"
                (onSelect)="onCandidateSelect($event)"
                (onClear)="onCandidateClear()"
                optionLabel="full_name"
                [minLength]="2"
                [delay]="350"
                [forceSelection]="true"
                [showClear]="true"
                [disabled]="!selectedArea"
                [emptyMessage]="'BOOKINGS.FORM.PASS_NO_RESULTS' | translate"
                [placeholder]="'BOOKINGS.FORM.PICK_PASS' | translate"
              >
                <ng-template let-candidate pTemplate="item">
                  <div class="candidate">
                    <span class="candidate-name" dir="auto">{{ candidate.full_name }}</span>
                    <span class="candidate-meta">
                      @if (candidate.mobile) {
                        <span dir="ltr">{{ candidate.mobile }}</span>
                      }
                      @if (candidate.client_role_name) {
                        <span class="candidate-role" dir="auto">{{ candidate.client_role_name }}</span>
                      }
                    </span>
                  </div>
                </ng-template>
              </p-autoComplete>
              <small class="field-hint">{{ (selectedArea ? 'BOOKINGS.FORM.PASS_SEARCH_HINT' : 'BOOKINGS.FORM.PICK_ROOM_FIRST') | translate }}</small>
            } @else {
              <div class="field-readonly" dir="auto">{{ lockedBookerName }}</div>
            }
          </div>
        </section>

        <section class="form-section">
          <h3 class="form-section-title">
            {{ 'BOOKINGS.FORM.SECTION_WHEN' | translate }}
            @if (durationLabel) {
              <span class="form-section-aside" dir="ltr">{{ durationLabel }}</span>
            }
          </h3>

          <!-- The pickers are on the CLIENT'S clock, not the admin's. Say so when they differ, or an
               admin abroad types 09:00 and gets a meeting at what is 09:00 to nobody. -->
          @if (bookingTime.differsFromBrowser) {
            <p class="field-note">
              <i aria-hidden="true" class="pi pi-globe"></i>
              {{ 'BOOKINGS.FORM.TIMEZONE_NOTE' | translate: { zone: bookingTime.label } }}
            </p>
          }

          <!-- appendTo="body" on both: the panel is wider than half this drawer, and the scrolling
               body clips anything that overflows it — so the End picker's calendar was cut off at
               the drawer edge. Appended to the body it is laid out against the VIEWPORT instead,
               which also lets PrimeNG flip it back inside the screen. Widening the drawer would
               only move the cut-off point, since the panel is positioned from the field. -->
          <div class="form-row">
            <div class="form-group">
              <label class="field-label" for="booking-start">{{ 'BOOKINGS.FORM.START' | translate }}</label>
              <p-datepicker
                inputId="booking-start"
                formControlName="start"
                appendTo="body"
                [showTime]="true"
                [stepMinute]="15"
                dateFormat="dd/mm/yy"
                [readonlyInput]="true"
              ></p-datepicker>
            </div>
            <div class="form-group">
              <label class="field-label" for="booking-end">{{ 'BOOKINGS.FORM.END' | translate }}</label>
              <p-datepicker
                inputId="booking-end"
                formControlName="end"
                appendTo="body"
                [showTime]="true"
                [stepMinute]="15"
                dateFormat="dd/mm/yy"
                [readonlyInput]="true"
              ></p-datepicker>
            </div>
          </div>

          @if (invalidRange) {
            <p class="field-error">{{ 'BOOKINGS.FORM.END_BEFORE_START' | translate }}</p>
          }

          <!-- Returned by PAPI on booking_conflict so another slot can be picked straight away. -->
          @if (busyRanges.length) {
            <div class="conflict">
              <strong class="conflict-title"><i aria-hidden="true" class="pi pi-exclamation-triangle"></i> {{ 'BOOKINGS.FORM.CONFLICT' | translate }}</strong>
              <ul class="conflict-list">
                @for (range of busyRanges; track range.start) {
                  <li dir="ltr">{{ wall(range.start) | date: 'dd/MM/yyyy HH:mm' }} &ndash; {{ wall(range.end) | date: 'HH:mm' }}</li>
                }
              </ul>
            </div>
          }
        </section>

        <!-- YB-2085. Offered only for a room with BOTH halves of its guest config set; a room
             without them refuses any non-empty list, so a field here would be a trap. -->
        @if (guestsEnabled) {
          <section class="form-section">
            <h3 class="form-section-title">
              {{ 'BOOKINGS.FORM.SECTION_GUESTS' | translate }}
              <span class="form-section-aside" dir="ltr">{{ invitees.length }}/{{ maxInvitees }}</span>
            </h3>

            <p class="field-note">
              <i aria-hidden="true" class="pi pi-info-circle"></i>
              {{ 'BOOKINGS.FORM.GUESTS_HINT' | translate }}
            </p>

            @if (!canEditInvitees) {
              <p class="field-error">{{ 'BOOKINGS.FORM.GUESTS_READONLY' | translate }}</p>
            }

            @if (invitees.length) {
              <ul class="invitee-list">
                @for (invitee of invitees; track invitee.mobile_hint + $index) {
                  <li class="invitee">
                    <span class="invitee-who">
                      <span class="invitee-name" dir="auto">{{ invitee.name || ('BOOKINGS.FORM.GUEST_UNNAMED' | translate) }}</span>
                      <!-- The hint, never the full number: the number exists here only so the list
                           can be sent back, and printing it adds nothing the admin needs to read. -->
                      <span class="invitee-hint" dir="ltr">&hellip;{{ invitee.mobile_hint }}</span>
                    </span>
                    <span class="invitee-meta">
                      <!-- guest = a pass was minted for this meeting; attendee = their own standing
                           pass already reaches the room, so nothing was created. PAPI re-answers
                           this on every save, including a reschedule. -->
                      <span class="invitee-kind" [class.is-attendee]="invitee.kind === 'attendee'">
                        {{ (invitee.kind === 'attendee' ? 'BOOKINGS.FORM.KIND_ATTENDEE' : 'BOOKINGS.FORM.KIND_GUEST') | translate }}
                      </span>
                      @if (canEditInvitees) {
                        <button
                          type="button"
                          class="btn btn-icon btn-link invitee-remove"
                          (click)="removeInvitee($index)"
                          [attr.aria-label]="'BOOKINGS.FORM.REMOVE_GUEST' | translate"
                        >
                          <i aria-hidden="true" class="pi pi-times"></i>
                        </button>
                      }
                    </span>
                  </li>
                }
              </ul>
            } @else {
              <p class="field-hint">{{ 'BOOKINGS.FORM.NO_GUESTS' | translate }}</p>
            }

            @if (canEditInvitees) {
              @if (inviteesFull) {
                <!-- Seats minus the booker. A room nobody has sized reports zero, so this is also
                     what an unsized room shows instead of an input that could only be refused. -->
                <p class="field-hint">{{ (maxInvitees ? 'BOOKINGS.FORM.GUESTS_FULL' : 'BOOKINGS.FORM.GUESTS_NO_SEATS') | translate: { max: maxInvitees } }}</p>
              } @else {
                <div class="invitee-add">
                  <app-mobile-with-country-code-input
                    [form]="inviteeForm"
                    [setDefaultCodeByClient]="true"
                    [required]="true"
                    [equalSize]="true"
                  ></app-mobile-with-country-code-input>

                  <div class="form-group">
                    <label class="field-label" for="invitee-name">{{ 'BOOKINGS.FORM.GUEST_NAME' | translate }}</label>
                    <input
                      id="invitee-name"
                      type="text"
                      pInputText
                      [formControl]="$any(inviteeForm.get('name'))"
                      [placeholder]="'BOOKINGS.FORM.GUEST_NAME_PLACEHOLDER' | translate"
                    />
                  </div>

                  @if (inviteeDuplicate) {
                    <p class="field-error">{{ 'BOOKINGS.FORM.GUEST_DUPLICATE' | translate }}</p>
                  }

                  <button type="button" class="btn btn-secondary invitee-add-btn" [disabled]="!canAddInvitee" (click)="addInvitee()">
                    <i aria-hidden="true" class="pi pi-plus"></i> {{ 'BOOKINGS.FORM.ADD_GUEST' | translate }}
                  </button>
                </div>
              }
            }
          </section>
        }

        <section class="form-section">
          <h3 class="form-section-title">{{ 'BOOKINGS.FORM.SECTION_DETAILS' | translate }}</h3>
          <div class="form-group">
            <label class="field-label" for="booking-title">{{ 'BOOKINGS.FIELDS.TITLE' | translate }}</label>
            <input id="booking-title" type="text" pInputText formControlName="title" [placeholder]="'BOOKINGS.FORM.TITLE_PLACEHOLDER' | translate" />
            <small class="field-hint">{{ 'BOOKINGS.FORM.TITLE_HINT' | translate }}</small>
          </div>
        </section>
      </div>
    </form>
  }
</p-drawer>
